import { Divider, Drawer, Tree, TreeProps } from "antd";
import FourGridIcon from "icons/FourGridIcon";
import { FC, useMemo, useState, useCallback } from "react";
// import YourFilter from "./YourFilter";
import { useGetCategory } from "services/category";
import useUserStore from "stores/user";
import { CircleArrowRight, X } from "lucide-react";
import useIsDesktopStore from "stores/isDesktop";
import {
  trackApplyFilter,
  trackClearFilter,
  trackFilterClick,
} from "../../utils/analytics";
interface ISideFilter {
  onChange: (value: string[]) => void;
  defaultValue?: string[];
}
interface IFilterComponent {
  hideYouFilter?: boolean;
}
const SideFilter: FC<ISideFilter> = ({ onChange, defaultValue = [] }) => {
  const { isDesktop } = useIsDesktopStore();

  const [checkedKeys, setCheckedKeys] = useState<string[]>(defaultValue);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const { user } = useUserStore();
  const { categoryList } = useGetCategory();

  // Handle tree node expansion/collapse
  const onExpand = (expandedKeys: React.Key[]) => {
    trackFilterClick("icon");
    setExpandedKeys(expandedKeys as string[]);
  };

  // Custom handler for tree node clicks
  const handleTreeNodeClick = useCallback(
    (
      nodeKey: string,
      isParent: boolean,
      hasChildren: boolean = true,
      parentId?: string
    ) => {
      if (isParent && hasChildren) {
        // For parent nodes with children, toggle expand/collapse
        const isExpanded = expandedKeys.includes(nodeKey);
        if (isExpanded) {
          // Collapse the node
          setExpandedKeys(expandedKeys.filter((key) => key !== nodeKey));
        } else {
          // Expand the node
          setExpandedKeys([...expandedKeys, nodeKey]);
        }
      } else {
        // For leaf nodes or parent nodes without children, toggle check state
        const newCheckedKeys = checkedKeys.includes(nodeKey)
          ? checkedKeys.filter((key) => key !== nodeKey && key !== parentId)
          : [...checkedKeys, nodeKey];

        setCheckedKeys(newCheckedKeys);
        onChange(newCheckedKeys);

        // Track filter application
        if (newCheckedKeys.length > 0) {
          trackApplyFilter("category", newCheckedKeys);
        }
      }
      trackFilterClick("title");
    },
    [checkedKeys, expandedKeys, onChange]
  );

  // Handle checkbox clicks - only leaf nodes have checkboxes now
  const onCheck: TreeProps["onCheck"] = (checkedKeys) => {
    trackFilterClick("checkbox");
    // Since only leaf nodes have checkboxes, proceed with normal checking
    const selectedKeys = checkedKeys as string[];
    setCheckedKeys(selectedKeys);
    onChange(selectedKeys);

    // Track filter application
    if (selectedKeys.length > 0) {
      trackApplyFilter("category", selectedKeys);
    }
  };

  const onClearFilter = () => {
    setCheckedKeys([]);
    setExpandedKeys([]); // Also reset expanded state when clearing
    onChange([]);

    // Track filter clearing
    trackClearFilter("category");
  };

  // Data structure for YourFilter component (uses label property)
  // const filterList = useMemo(() => {
  //   if (categoryList) {
  //     if (user && user.userTierId !== 5) {
  //       return categoryList.map((category) => ({
  //         key: String(category.id), // convert id to string for key
  //         label: category.name, // use name for the label
  //         children: category.subCategories.map((subCategory) => ({
  //           key: String(subCategory.id), // combine parent and subcategory ids
  //           label: subCategory.name, // use subcategory name for label
  //           parentId: String(subCategory.parentCategoryId),
  //         })),
  //       }));
  //     } else {
  //       return categoryList
  //         .filter((c) => c.name.trim() !== "ยาแผนปัจจุบัน")
  //         .map((category) => ({
  //           key: String(category.id),
  //           label: category.name,
  //           children: category.subCategories.map((subCategory) => ({
  //             key: String(subCategory.id),
  //             label: subCategory.name,
  //             parentId: String(subCategory.parentCategoryId),
  //           })),
  //         }));
  //     }
  //   }
  //   return [];
  // }, [categoryList, user]);

  // Data structure for Tree component (uses title property with custom click handlers)
  const cateList = useMemo(() => {
    if (categoryList) {
      if (user && user.userTierId !== 5) {
        return categoryList.map((category) => ({
          key: String(category.id), // convert id to string for key
          checkable: true, //
          title: (
            <span
              className="is-parent"
              onClick={(e) => {
                e.stopPropagation();
                handleTreeNodeClick(
                  String(category.id),
                  true,
                  category.subCategories.length > 0
                );
              }}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              {category.name}
            </span>
          ),
          children: category.subCategories.map((subCategory) => ({
            key: String(subCategory.id), // combine parent and subcategory ids
            checkable: true, // Show checkbox for leaf nodes
            title: (
              <span
                className="is-leaf"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTreeNodeClick(
                    String(subCategory.id),
                    false,
                    false,
                    String(subCategory.parentCategoryId)
                  );
                }}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                {subCategory.name}
              </span>
            ),
            parentId: String(subCategory.parentCategoryId),
          })),
        }));
      } else {
        return categoryList.map((category) => ({
          key: String(category.id),
          checkable: true, //
          title: (
            <span
              className="is-parent"
              onClick={(e) => {
                e.stopPropagation();
                handleTreeNodeClick(
                  String(category.id),
                  true,
                  category.subCategories.length > 0
                );
              }}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              {category.name}
            </span>
          ),
          children: category.subCategories.map((subCategory) => ({
            key: String(subCategory.id),
            checkable: true, // Show checkbox for leaf nodes
            title: (
              <span
                className="is-leaf"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTreeNodeClick(
                    String(subCategory.id),
                    false,
                    false,
                    String(subCategory.parentCategoryId)
                  );
                }}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                {subCategory.name}
              </span>
            ),
            parentId: String(subCategory.parentCategoryId),
          })),
        }));
      }
    }
    return [];
  }, [categoryList, user, handleTreeNodeClick]);

  // const handleOnChangeFilter = (value: string[]) => {
  //   setCheckedKeys(value as string[]);
  //   onChange(value as string[]);

  //   // If clearing all filters, also reset expanded state
  //   if (value.length === 0) {
  //     setExpandedKeys([]);
  //   }

  //   // Track filter application or clearing
  //   if (value.length > 0) {
  //     trackApplyFilter("category", value);
  //   } else {
  //     trackClearFilter("category");
  //   }
  // };

  const [open, setOpen] = useState(false);

  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const FilterComponent: FC<IFilterComponent> = () => {
    return (
      <div className="filter-component">
        {/* {!hideYouFilter && <></>} */}

        <div className="w-full xl:w-[250px] bg-white p-2 rounded-md mt-10 pb-20">
          <div className="flex justify-between">
            <div className="gap-2 flex">
              <FourGridIcon />
              ประเภทของสินค้า
            </div>

            {checkedKeys.length > 0 && (
              <div
                className="hidden xl:block cursor-pointer text-primary-800"
                onClick={onClearFilter}
              >
                Clear
              </div>
            )}
            <div className="block xl:hidden cursor-pointer" onClick={onClose}>
              <X />
            </div>
          </div>

          <Divider className="my-4" />
          <Tree
            checkable
            selectable={false}
            onCheck={onCheck}
            onExpand={onExpand}
            checkedKeys={checkedKeys}
            expandedKeys={expandedKeys}
            treeData={cateList}
          />
        </div>
        {/* <div className="absolute bottom-0 left-0 w-full bg-[#F2F2F2] flex gap-2 p-2 h-14 items-center justify-center">
          <Button type="primary" className="px-10">
            แสดงผล
          </Button>
          <Button danger className="px-10">
            ล้าง
          </Button>
        </div> */}
      </div>
    );
  };

  if (isDesktop) {
    return <FilterComponent hideYouFilter />;
  }
  return (
    <div>
      <div className="flex items-center gap-4 justify-between">
        <div className="font-semibold text-[#888888]">สินค้าทั้งหมด</div>
        <div
          className="text-primary flex gap-1 items-center cursor-pointer"
          onClick={showDrawer}
        >
          เลือกหมวดหมู่
          <CircleArrowRight />
        </div>
      </div>
      <Drawer
        placement={"right"}
        onClose={onClose}
        open={open}
        styles={{
          header: {
            display: "none",
          },
          body: {
            padding: 0,
          },
        }}
      >
        <FilterComponent hideYouFilter />
      </Drawer>
    </div>
  );
};
export default SideFilter;
